/**
 * The WebSocket connection.
 *
 * A plain class rather than a React hook, and deliberately so. A socket is a
 * resource with a lifecycle that does not match a component's: it must survive a
 * re-render, it must not be recreated when a dependency array changes by
 * accident, and its reconnect timer must be cancelled on unmount. Wrapping that
 * in `useEffect` and hoping the dependency array is right is how a board ends up
 * with two sockets after a hot reload. The hook in `state/BoardProvider.tsx`
 * owns exactly one instance and hands it out; this class does the work.
 *
 * ## What it is responsible for
 *
 * 1. **The handshake.** `hello` carries `since` - the highest sequence the client
 *    has applied - and `resumeClientId`, which keeps presence identity stable
 *    across a reconnect.
 * 2. **Reconnection.** Exponential backoff with full jitter, plus immediate
 *    retry when the browser reports that the network came back.
 * 3. **Liveness.** A client-side ping every 25 seconds. A TCP connection that
 *    dies without a close frame - a dropped Wi-Fi association, a laptop lid -
 *    leaves a socket that looks open and never fires `close`. The ping is the
 *    only thing that detects it, and without it the board silently stops
 *    updating while the UI claims to be connected.
 * 4. **Cursor throttling.** See `lib/coalescer.ts`.
 * 5. **Close-code classification.** A terminal code means do not reconnect.
 *
 * ## What it is not responsible for
 *
 * It does not interpret board state. Every frame goes to `onMessage` unchanged
 * and the reducer decides what it means. The one exception is `welcome`, which
 * the connection must read in order to know its own `clientId` and to reset the
 * backoff counter - those are facts about the connection, not about the board.
 */

import type { ClientMessage, ConnectionStatus, CursorPosition, ServerMessage } from '@slate/shared';
import {
  CLOSE_CODES,
  CURSOR_THROTTLE_MS,
  HEARTBEAT_INTERVAL_MS,
  TERMINAL_CLOSE_CODES,
  serverMessageSchema,
} from '@slate/shared';
import { Coalescer } from '../lib/coalescer.js';
import { backoffDelay, isRetryableClose } from '../lib/backoff.js';

export interface BoardSocketOptions {
  boardId: string;
  /** Resolved lazily on every connect, because the access token rotates and a
   * captured value would be the one from the first connection. */
  getToken(): string | null;
  /** The highest sequence applied locally. Sent as `since` on every `hello`. */
  getSince(): number;
  /** The `clientId` from a previous `welcome`, if any. */
  getResumeClientId(): string | null;
  onMessage(message: ServerMessage): void;
  onStatus(status: ConnectionStatus): void;
  /** A terminal close. The caller should stop trying and explain why. */
  onFatal(reason: string): void;
  /** Round-trip time in milliseconds, measured from the `pong` echo. */
  onLatency(ms: number): void;
}

/** How many consecutive missed pongs before the socket is considered dead. Two
 * intervals is the smallest number that cannot be explained by one delayed
 * frame. */
const MISSED_PONG_LIMIT = 2;

export class BoardSocket {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private missedPongs = 0;
  private clientId: string | null = null;
  private closedByUs = false;
  private status: ConnectionStatus = 'offline';
  private cursorsEnabled = true;
  /** Mirrors the server's `ready` flag on the socket attachment. Frames other
   * than `welcome` and `pong` are dropped until the handshake completes, because
   * a broadcast delivered before `welcome` would be applied to a client that has
   * not yet been told which sequence it is resuming from - and the replay inside
   * `welcome` would then apply the same events again, out of order. */
  private welcomed = false;

  private readonly cursorCoalescer: Coalescer<CursorPosition>;

  constructor(private readonly options: BoardSocketOptions) {
    this.cursorCoalescer = new Coalescer<CursorPosition>(CURSOR_THROTTLE_MS, (position) => {
      this.send({ t: 'cursor', d: position });
    });
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  connect(): void {
    if (this.socket !== null) return;
    this.closedByUs = false;
    this.welcomed = false;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const token = this.options.getToken();
    if (token === null) {
      // No token means the session is gone. Reconnecting would produce a 401 on
      // every attempt, so this is terminal by construction.
      this.setStatus('offline');
      this.options.onFatal('Your session has ended. Sign in again.');
      return;
    }

    const url = new URL(`/api/boards/${this.options.boardId}/socket`, window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    // The browser `WebSocket` constructor cannot set request headers, so the
    // token travels as a query parameter. See `apps/api/src/routes/socket.ts`
    // for why this was chosen over a cookie or a subprotocol, and for the
    // mitigation (a 15-minute lifetime).
    url.searchParams.set('access_token', token);

    let socket: WebSocket;
    try {
      socket = new WebSocket(url.toString());
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;
    socket.onopen = () => this.handleOpen(socket);
    socket.onmessage = (event) => this.handleFrame(event.data);
    socket.onclose = (event) => this.handleClose(event.code, event.reason);
    socket.onerror = () => {
      // The browser fires `error` immediately before `close` for every failure
      // mode, and the close handler is where the classification lives. Logging
      // here and acting there keeps one decision point instead of two that can
      // disagree.
    };
  }

  /** Intentional teardown: unmount, sign-out, board change. Suppresses reconnect
   * and flushes nothing, because the position is meaningless to a server that no
   * longer has the socket. */
  close(): void {
    this.closedByUs = true;
    this.clearReconnect();
    this.stopHeartbeat();
    this.cursorCoalescer.cancel();

    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      // `NORMAL` is in `TERMINAL_CLOSE_CODES`, so the server suppresses the
      // presence-leave broadcast for it. That is the desired behaviour: a tab
      // navigating away is not the same event as a socket dying, and the server
      // will not report a leave that nobody needs to see - the socket is gone
      // either way and the presence list is derived from live sockets.
      try {
        socket.close(CLOSE_CODES.NORMAL, 'client closed');
      } catch {
        // Already closing. Nothing useful to do.
      }
    }
    this.setStatus('offline');
  }

  get isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  get currentStatus(): ConnectionStatus {
    return this.status;
  }

  get currentClientId(): string | null {
    return this.clientId;
  }

  // -------------------------------------------------------------------------
  // Sending
  // -------------------------------------------------------------------------

  /** Send a frame. Returns `false` when the socket is not open, which is the
   * signal for the caller to fall back to HTTP. */
  send(message: ClientMessage): boolean {
    if (this.socket === null || this.socket.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch {
      // A socket can be closing between the readyState check and the send.
      return false;
    }
  }

  /**
   * Offer a cursor position.
   *
   * Coalesced to 20 Hz locally and again by the server. The duplication is
   * intentional: the client's throttle is what keeps the frames off the network,
   * and the server's is what stops a client that ignores its own throttle from
   * setting the rate for everyone. See `handleCursor` in the Durable Object.
   */
  sendCursor(position: CursorPosition): void {
    if (!this.cursorsEnabled) return;
    if (!this.isOpen) return;
    this.cursorCoalescer.push(position);
  }

  /** Flush the buffered cursor immediately. Called when the pointer leaves the
   * board, so the last position is delivered without waiting out the window. */
  flushCursor(): void {
    this.cursorCoalescer.flushNow();
  }

  /** Cursor frames are suppressed while the tab is hidden. A background tab has
   * no pointer, and a board with five hidden tabs should not carry five idle
   * cursor streams. */
  setCursorsEnabled(enabled: boolean): void {
    this.cursorsEnabled = enabled;
    if (!enabled) this.cursorCoalescer.cancel();
  }

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  private handleOpen(socket: WebSocket): void {
    // The handshake. `since` is read at this moment rather than at `connect()`
    // time, so the reconnect that follows a long backoff sends the sequence the
    // client has actually applied - which may have advanced via an HTTP
    // fallback mutation while the socket was down.
    const sent = this.trySend(socket, {
      t: 'hello',
      d: {
        since: this.options.getSince(),
        resumeClientId: this.options.getResumeClientId(),
        cursor: null,
      },
    });

    if (!sent) {
      this.handleClose(1006, 'handshake send failed');
      return;
    }

    this.startHeartbeat();
  }

  private handleFrame(data: unknown): void {
    if (typeof data !== 'string') {
      // Binary frames are not part of this protocol. The server rejects them
      // too; ignoring one here rather than throwing keeps a rogue frame from
      // tearing down an otherwise healthy connection.
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }

    // Validated rather than cast. The socket is an input channel like any other,
    // and `z.discriminatedUnion` dispatches on the tag in constant time, so the
    // cost is one small allocation per frame. The alternative is a reducer that
    // trusts whatever arrived, which is how a client ends up rendering
    // `undefined.title` from a frame sent by a newer server.
    const result = serverMessageSchema.safeParse(parsed);
    if (!result.success) return;
    const message = result.data;

    if (message.t === 'welcome') {
      this.clientId = message.d.clientId;
      this.welcomed = true;
      // Reset only on a completed handshake, not on `open`. A socket that opens
      // and is immediately closed by an auth failure would otherwise reset the
      // backoff and reconnect in a tight loop.
      this.attempt = 0;
      this.missedPongs = 0;
      this.setStatus('connected');
    } else if (!this.welcomed) {
      // See `welcomed`. `pong` is allowed through only because the heartbeat can
      // legitimately race the handshake on a fast connection, and it carries no
      // board state.
      if (message.t !== 'pong') return;
    }

    if (message.t === 'pong') {
      this.missedPongs = 0;
      // The server echoes the client's own clock value back, so the round trip
      // is measured without either side needing to agree about the time.
      this.options.onLatency(Math.max(0, Date.now() - message.d.ts));
    }

    this.options.onMessage(message);
  }

  private handleClose(code: number, reason: string): void {
    this.socket = null;
    this.stopHeartbeat();
    this.cursorCoalescer.cancel();

    if (this.closedByUs) {
      this.setStatus('offline');
      return;
    }

    if (!isRetryableClose(code, TERMINAL_CLOSE_CODES)) {
      this.setStatus('offline');
      this.options.onFatal(terminalReason(code, reason));
      return;
    }

    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.closedByUs) return;
    if (this.reconnectTimer !== null) return;

    this.setStatus('reconnecting');
    const delay = backoffDelay(this.attempt);
    this.attempt += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // `attempt` is not reset here. It is reset on `welcome`, so a server that
      // accepts the TCP connection and then immediately closes it - a deploy in
      // progress - backs off rather than spinning.
      this.connect();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // Heartbeat
  // -------------------------------------------------------------------------

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.missedPongs = 0;

    this.heartbeatTimer = setInterval(() => {
      if (this.socket === null || this.socket.readyState !== WebSocket.OPEN) return;

      // A socket that has not answered the last two pings is dead even though
      // `readyState` says otherwise. Closing with `GOING_AWAY` is honest: it is
      // retryable, and the reason the client is closing is that it can no longer
      // tell whether the connection works.
      if (this.missedPongs >= MISSED_PONG_LIMIT) {
        this.missedPongs = 0;
        try {
          this.socket.close(CLOSE_CODES.GOING_AWAY, 'heartbeat timeout');
        } catch {
          this.handleClose(1006, 'heartbeat close failed');
        }
        return;
      }

      if (this.trySend(this.socket, { t: 'ping', d: { ts: Date.now() } })) {
        this.missedPongs += 1;
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private trySend(socket: WebSocket, message: ClientMessage): boolean {
    try {
      socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus(status);
  }
}

/** A close code is a number and a human needs a sentence. The mapping lives here
 * rather than in the UI so that a new code is a compile error at one site. */
function terminalReason(code: number, reason: string): string {
  switch (code) {
    case CLOSE_CODES.UNAUTHORIZED:
      return 'Your session has expired. Sign in again to keep working on this board.';
    case CLOSE_CODES.FORBIDDEN:
      return 'You no longer have access to this board.';
    case CLOSE_CODES.PROTOCOL_ERROR:
      return 'This tab is out of date and cannot talk to the server. Reload the page.';
    case CLOSE_CODES.NORMAL:
      return reason === '' ? 'The connection was closed.' : reason;
    default:
      return 'The connection was closed and will not be retried.';
  }
}
